import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

import { isEmpty } from '@ember/utils';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import type ToasterService from '@linc-technologies/linced-common/services/toaster';
import config from '@linc-technologies/linced-common/utils/config';
import { isMobile } from '../utils/platform';

// TokenClient is the GIS token client. Google's samples set `callback` after
// init, which the published types leave out.
type TokenClient = google.accounts.oauth2.TokenClient & {
  callback: (response: google.accounts.oauth2.TokenResponse) => void;
};

// DriveMedia is the media record fields for a picked Google Drive file. The
// caller adds the owner fields (school, person, sharing level).
export interface DriveMedia {
  mediaType: number;
  mediaSource: number;
  mimeType: string;
  fileName: string;
  storageKey: string;
  resourceKey: string;
  fileSize: number;
  thumbnailUrl: string;
}

// ShareRequest is an open "share this file?" dialog. The dialog resolves it
// when the user confirms, or rejects it when the user cancels.
export interface ShareRequest {
  fileName: string;
  thumbnailUrl: string;
  resolve: () => void;
  reject: (reason: { userCancelled: true }) => void;
}

// PickFilesOptions are the options for pickFiles.
export interface PickFilesOptions {
  // onLoaded is called when the Picker is ready to pick.
  onLoaded?: () => void;
}

declare global {
  interface Window {
    // googleDriveNativeAuth is the Google Drive service that waits for a token
    // from the iOS app. sendGoogleAccessToken in
    // linced-common/addon/vendor/native-bridge.js gives it the token.
    googleDriveNativeAuth?: GoogleDriveService;
  }
}

// GoogleDriveService lets the user pick Google Drive files and embed them.
// It loads the Google scripts, gets the OAuth token, shows the Picker and
// shares the picked files.
export default class GoogleDriveService extends Service {
  // Services

  @service declare toaster: ToasterService;

  // Constants

  readonly _apiKey = config.googleApiKey as string;

  readonly _clientId = config.googleClientId as string;

  readonly _appId = this._clientId.split('-')[0] ?? '';

  readonly _apiUrl = 'https://apis.google.com/js/api.js';

  readonly _gisUrl = 'https://accounts.google.com/gsi/client';

  readonly _driveDiscoveryDocument =
    'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest';

  readonly _scopes = ['https://www.googleapis.com/auth/drive.file'];

  readonly _defaultTokenExpiry = 55 * 60 * 1000; // 55 minutes (60 minutes with leeway).

  readonly _tokenExpiryLeeway = 5 * 60 * 1000; // 5 minute leeway.

  // State

  @tracked
  _apiLoaded = false;

  @tracked
  _apiLoading = false;

  _apiLoadingPromise: Promise<void> | null = null;

  @tracked
  _gisLoaded = false;

  @tracked
  _gisLoading = false;

  _gisLoadingPromise: Promise<void> | null = null;

  @tracked
  _tokenClient: TokenClient | null = null;

  @tracked
  _token: string | null = null;

  // _tokenExpiresAt is the time (ms since epoch) after which the token is not
  // used again.
  _tokenExpiresAt = 0;

  // shareRequest is the open "share this file?" dialog, or null. Refer:
  // components/google-drive-share-confirm-dialog.
  @tracked
  shareRequest: ShareRequest | null = null;

  _resolveNativeToken: () => void = () => {};

  _rejectNativeToken: () => void = () => {};

  // Public methods

  // isPickerSupported is false inside the native app webviews. Google blocks
  // the web Picker in embedded webviews (enforced from 16 November 2026), so
  // the app must not show it there. Refer: DEV-369.
  get isPickerSupported() {
    return !isMobile();
  }

  // pickFiles shows the Picker, and gets a token first if necessary.
  //
  // @return the files the user picked, or none if the user closed the Picker.
  async pickFiles({ onLoaded }: PickFilesOptions = {}): Promise<
    google.picker.DocumentObject[]
  > {
    if (!this._hasValidToken) {
      await this._requestToken();
    }
    await this._loadGapiModule('picker');

    return new Promise((resolve) => {
      const docsView = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setOwnedByMe(true);

      const teamDriveView = new google.picker.DocsView(
        google.picker.ViewId.DOCS,
      )
        .setIncludeFolders(true)
        .setEnableDrives(true);

      const picker = new google.picker.PickerBuilder()
        .addView(docsView)
        .addView(teamDriveView)
        .addView(google.picker.ViewId.DOCS_IMAGES)
        .addView(google.picker.ViewId.DOCUMENTS)
        .addView(google.picker.ViewId.PRESENTATIONS)
        .addView(google.picker.ViewId.SPREADSHEETS)
        .addView(google.picker.ViewId.FORMS)
        .addView(google.picker.ViewId.PDFS)
        .addView(google.picker.ViewId.DOCS_VIDEOS)
        .setOAuthToken(this._token as string)
        .setDeveloperKey(this._apiKey)
        .setAppId(this._appId)
        .setCallback((data) => {
          // ACTION is typed as Action | string, as Google also sends
          // undocumented actions (loaded).
          switch (data[google.picker.Response.ACTION]) {
            case 'loaded':
              onLoaded?.();
              break;
            case google.picker.Action.PICKED as string:
              resolve(data[google.picker.Response.DOCUMENTS] ?? []);
              break;
            case google.picker.Action.CANCEL as string:
            case google.picker.Action.ERROR as string:
              resolve([]);
              break;
          }
        })
        .build();

      picker.setVisible(true);
    });
  }

  // embedFile prepares a picked Google Drive file to embed. It shares the
  // file with "anyone with the link" (the user confirms first if the file is
  // private), so that the people who see the media can open it.
  //
  // @param file - the picked file.
  // @param onShareFailed - optional. Called with the Drive status when the
  //   file cannot be shared. Resolve it to embed the file anyway, or reject it
  //   to stop. When omitted, the share error is thrown.
  // @return the media record fields, or null if the user cancelled.
  async embedFile(
    file: google.picker.DocumentObject,
    onShareFailed?: (status?: number) => Promise<unknown>,
  ): Promise<DriveMedia | null> {
    const fileId = file[google.picker.Document.ID];
    const mimeType = file[google.picker.Document.MIME_TYPE] ?? '';
    let thumbnailUrl = '';

    try {
      await this._loadDriveClient();
      // Ask the user first, since this changes the sharing on their own
      // document.
      await this._shareWithLink(fileId, (fileName, thumbnail) =>
        this._requestShare(fileName, thumbnail),
      );
      // Get thumbnail URL. Only try this once the permission grant above has
      // landed, as Drive bakes the file's sharing state into the thumbnail
      // link - one minted while the file is still private returns 403 for
      // everyone else. It doesn't matter if this fails, we just won't have a
      // thumbnail for this content.
      thumbnailUrl = (await this._thumbnailUrl(fileId)) ?? '';
    } catch (err) {
      if ((err as { userCancelled?: boolean } | null)?.userCancelled) {
        return null;
      }
      if (!onShareFailed) {
        throw err;
      }
      // We weren't able to update the sharing permissions.
      await onShareFailed((err as { status?: number } | null)?.status);
    }

    let mediaType: number =
      MediaType.getValue(mimeType.split('/')[0] ?? '') || MediaType.FILE;
    if (this._isPreviewable(mimeType)) {
      mediaType = MediaType.EMBED;
    }

    return {
      mediaType,
      mediaSource: MediaSource.GOOGLE_DRIVE,
      mimeType,
      fileName: file[google.picker.Document.NAME] ?? '',
      storageKey: fileId,
      resourceKey: file.resourceKey ?? '',
      fileSize: 0,
      thumbnailUrl,
    };
  }

  // showEmbedError tells the user why a Google Drive file was not embedded.
  // It shows nothing when the user cancelled.
  //
  // @param err - the error from pickFiles or embedFile.
  // @param file - optional. The file that was not embedded, to name it in the
  //   message.
  showEmbedError(err: unknown, file?: google.picker.DocumentObject) {
    const reason = err as { userCancelled?: boolean; cannotShare?: boolean };
    if (reason?.userCancelled) {
      return;
    }

    // Document.NAME is `name`. Read it directly, as the Picker script may not
    // be loaded when the Picker fails.
    const fileName = file?.name;
    const subject = fileName ? `"${fileName}"` : 'that file';
    if (reason?.cannotShare) {
      this.toaster.error(
        `You don't have permission to change sharing on ${subject}, so it ` +
          'cannot be embedded. Ask its owner to share it, or save a copy to ' +
          'your own Drive.',
      );
      return;
    }

    this.toaster.error(
      `We couldn't embed ${subject} from Google Drive. Try again, or upload ` +
        'the file instead.',
    );
  }

  // confirmShare closes the share dialog, and lets the embed share the file.
  confirmShare() {
    this.shareRequest?.resolve();
    this.shareRequest = null;
  }

  // cancelShare closes the share dialog, and stops the embed.
  cancelShare() {
    this.shareRequest?.reject({ userCancelled: true });
    this.shareRequest = null;
  }

  /*
    When using Native OAuth SDK, we need to set the GAPI token
    manually.
  */
  // The iOS app sends the token to sendGoogleAccessToken in
  // vendor/native-bridge.js, which calls this.
  receiveNativeToken(token: string) {
    if (!isEmpty(token)) {
      this._token = token;

      // Expire the token before Google does.  We don't receive expiry
      // details via native auth, so we'll assume it's a standard 1-hour token.
      this._setTokenExpiry(this._defaultTokenExpiry);

      this._loadGapi()
        .then(() => {
          gapi.client.setToken({ access_token: token });
          this._resolveNativeToken();
        })
        .catch(() => {
          this._rejectNativeToken();
        });
    } else {
      this._rejectNativeToken();
    }
  }

  // Private methods

  _loadGapi(): Promise<void> {
    if (this._apiLoaded) {
      return Promise.resolve();
    } else if (!this._apiLoading) {
      this._apiLoading = true;
      this._apiLoadingPromise = new Promise<void>((resolve) => {
        const apiScriptElement = document.createElement('script');

        apiScriptElement.src = this._apiUrl;
        apiScriptElement.onload = () => {
          this._apiLoaded = true;
          this._apiLoading = false;
          return gapi.load('client', resolve);
        };
        document.body.appendChild(apiScriptElement);
      });
    }

    return this._apiLoadingPromise as Promise<void>;
  }

  _loadGapiModule(module: string): Promise<void> {
    return this._loadGapi().then(() => {
      return new Promise<void>((resolve) => {
        gapi.load(module, resolve);
      });
    });
  }

  _loadIdentityServices(): Promise<void> {
    if (isMobile() || this._gisLoaded) {
      return Promise.resolve();
    } else if (!this._gisLoading) {
      this._gisLoading = true;
      this._gisLoadingPromise = new Promise<void>((resolve) => {
        const gisScriptElement = document.createElement('script');

        gisScriptElement.src = this._gisUrl;
        gisScriptElement.onload = () => {
          this._gisLoaded = true;
          this._gisLoading = false;
          return resolve();
        };
        document.body.appendChild(gisScriptElement);
      }).then(() => {
        const tokenClient = google.accounts.oauth2.initTokenClient({
          client_id: this._clientId,
          scope: this._scopes.join(' '),
          // Ask for exactly the scopes above and nothing else. Left to its
          // default, Google folds every scope the user has ever granted this
          // client into the request. Accounts that authorised Hero before
          // youtube was dropped (May 2021) still carry that grant, and Google
          // now rejects drive.file and youtube together with a 400
          // invalid_request, blocking the Drive picker entirely.
          include_granted_scopes: false,
          callback: () => {}, // defined later
        }) as TokenClient;
        this._tokenClient = tokenClient;
      });
    }

    return this._gisLoadingPromise as Promise<void>;
  }

  _loadDriveClient() {
    // Client is now initialised via _loadGapi(), but GIS is also required.
    return Promise.all([this._loadGapi(), this._loadIdentityServices()]).then(
      () => {
        return gapi.client.load(this._driveDiscoveryDocument);
      },
    );
  }

  _requestToken(): Promise<void> {
    if (isMobile()) {
      /*
        When the native code comes back with a token,
        assign this token and resolve _requestToken.
      */
      window.googleDriveNativeAuth = this;
      return new Promise<void>((resolve, reject) => {
        this._resolveNativeToken = resolve;
        this._rejectNativeToken = reject;
        window.open('https://www.accounts.google.com');
      });
    } else {
      return Promise.all([this._loadGapi(), this._loadIdentityServices()]).then(
        () => {
          return new Promise<void>((resolve, reject) => {
            const tokenClient = this._tokenClient as TokenClient;
            tokenClient.callback = (response) => {
              if (response.error !== undefined) {
                // Callers read the GIS error fields, so reject with the response.
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                return reject(response);
              }
              this._token = response.access_token;

              // Expire the token before Google does. The types give
              // expires_in as a string, so accept a number or a numeric string.
              let expiry = this._defaultTokenExpiry;
              const expiresIn = Number(response.expires_in);
              if (Number.isInteger(expiresIn) && expiresIn > 0) {
                // We received a valid-looking expiry.  Use it.
                expiry = expiresIn * 1000;
                if (expiry > this._tokenExpiryLeeway) {
                  // The token has long enough left to apply our leeway.
                  expiry = expiry - this._tokenExpiryLeeway;
                }
              }
              this._setTokenExpiry(expiry);

              return resolve();
            };

            tokenClient.requestAccessToken({ prompt: '' });
          });
        },
      );
    }
  }

  // _setTokenExpiry sets when the token expires, in ms from now. After that,
  // _hasValidToken is false and the next request reauthenticates. This works
  // around token expiry without reauthentication on every request / picker
  // launch, and needs no timer.
  _setTokenExpiry(delay: number) {
    this._tokenExpiresAt = Date.now() + delay;
  }

  get _hasValidToken() {
    return !isEmpty(this._token) && Date.now() < this._tokenExpiresAt;
  }

  // _requestShare opens the share dialog. It resolves if the user confirms
  // sharing, or rejects if they cancel.
  _requestShare(fileName: string, thumbnailUrl: string) {
    if (this.shareRequest) {
      // A confirmation is already in progress; reject the new request immediately.
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject({ userCancelled: true });
    }
    return new Promise<void>((resolve, reject) => {
      this.shareRequest = { fileName, thumbnailUrl, resolve, reject };
    });
  }

  // _shareWithLink ensures that the file is set to "viewable by anyone with
  // the link", so that it will be viewable by other (authorised) users.
  //
  // @param {string} fileId - the id of the file to create permissions for.
  // @param {Function} [confirmFn] - optional async function called with
  //   (fileName: string, thumbnailUrl: string). Should resolve if the user
  //   confirms sharing, or reject if they cancel. When omitted the permission
  //   is created directly without a confirmation dialog.
  // @return {Promise} Promise object represents the created drive permissions.
  _shareWithLink(
    fileId: string,
    confirmFn?: (fileName: string, thumbnailUrl: string) => Promise<unknown>,
  ) {
    const createPermission = () =>
      gapi.client.drive.permissions.create({
        fileId,
        resource: {
          type: 'anyone',
          role: 'reader',
          // Anyone with the link, not found by search. Drive v3 dropped the v2
          // `withLink` field for this.
          allowFileDiscovery: false,
        },
        supportsAllDrives: true,
      });

    return this._isSharedWithLink(fileId)
      .catch(() => false)
      .then((alreadyPublic): Promise<unknown> => {
        if (alreadyPublic) {
          // Permission already exists, nothing to do. Checked before
          // canShare, as a file that is already shared needs no change even
          // when the current user has no rights to re-share it.
          return Promise.resolve();
        }

        return this._canShare(fileId).then((canShare) => {
          if (!canShare) {
            // Drive would reject the permission change with a 403, so stop
            // before making it and hand the caller a reason it can explain.
            // `status` is retained for callers that branch on a 403.
            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
            return Promise.reject({ cannotShare: true, status: 403 });
          }

          if (!confirmFn) {
            // No dialog callback supplied, ensure the permission directly.
            return createPermission();
          }

          // File is private, fetch display info then show confirmation dialog.
          return gapi.client.drive.files
            .get({
              fileId,
              fields: 'name,hasThumbnail,thumbnailLink',
              supportsAllDrives: true,
            })
            .then((response) => {
              const fileName = response.result.name || '';
              const thumbnailUrl = response.result.hasThumbnail
                ? (response.result.thumbnailLink ?? '')
                : '';
              return confirmFn(fileName, thumbnailUrl);
            })
            .then(() => createPermission());
        });
      });
  }

  // _canShare checks whether the current user is allowed to change the file's
  // sharing settings, so callers can fail fast with a clear message rather
  // than surfacing a raw Drive 403.
  //
  // @param {string} fileId - the id of the file to check.
  // @return {Promise<boolean>} resolves false only when Drive explicitly
  //   reports that the user cannot share. Resolves true when the check itself
  //   fails, so a failed pre-flight never blocks an otherwise valid embed.
  _canShare(fileId: string) {
    return gapi.client.drive.files
      .get({
        fileId,
        fields: 'capabilities(canShare)',
        supportsAllDrives: true,
      })
      .then((response) => {
        const capabilities = response.result.capabilities || {};
        return capabilities.canShare !== false;
      })
      .catch(() => true);
  }

  // _isSharedWithLink checks whether the file already has the "viewable by
  // anyone with the link" permission set.
  //
  // @param {string} fileId - the id of the file to check permissions for.
  // @return {Promise<boolean>} Promise resolving to true if the permission exists.
  _isSharedWithLink(fileId: string) {
    return gapi.client.drive.permissions
      .list({
        fileId,
        fields: 'permissions(type,role)',
        supportsAllDrives: true,
      })
      .then((response) => {
        const permissions = response.result.permissions || [];
        return permissions.some((p) => p.type === 'anyone');
      });
  }

  // _thumbnailUrl attempts to get the thumbnail URL for the specified Google
  // Drive file.  It doesn't matter if this fails - we just won't have a
  // thumbnail for this content.
  _thumbnailUrl(fileId: string) {
    return gapi.client.drive.files
      .get({
        fileId,
        fields: 'hasThumbnail,thumbnailLink',
        supportsAllDrives: true,
      })
      .then((response) => {
        if (response.result.hasThumbnail) {
          return response.result.thumbnailLink;
        }
        return '';
      })
      .catch(() => {
        return '';
      });
  }

  // _isPreviewable determines whether a file can/should be embedded using
  // Google Drive's "preview" viewer, based on mime type.
  // Note:
  // - images should not be embedded in this way in order to be usable in <img>
  //   tags.
  //
  // @param {string} mimeType - The File's mimetype.
  // @return {boolean} whether the file is embeddable using the preview viewer.
  _isPreviewable(mimeType: string) {
    if (/^application\/(pdf|rtf|x-youtube)$/i.test(mimeType)) {
      // standalone file type which is embeddable via Google Drive, or a
      // YouTube video (which is also embeddable).
      return true;
    } else if (/^application\/vnd\.google-apps\./i.test(mimeType)) {
      // Google Docs / Sheets / etc.
      return true;
    } else if (
      /^application\/vnd\.openxmlformats-officedocument\./i.test(mimeType)
    ) {
      // Modern (openXML) MS Office file (Word/Excel/Powerpoint).
      return true;
    } else if (
      /^application\/(msword|vnd\.ms-(excel|powerpoint))/i.test(mimeType)
    ) {
      // Legacy or macro-enabled MS Office file (Word/Excel/Powerpoint).
      return true;
    } else if (/^(video|audio)\//i.test(mimeType)) {
      // Video or audio - let Google handle the embedding of these.
      return true;
    }

    return false;
  }
}
